/** PROTOTYPE live Platform / Retention build-up -- presentational, from the engine.
 *  Project-level lines live in breakdown.projectLevel.recurring.components. The Cost view
 *  shows the cost build-up (infra + support -> cost/mo), the Price view the price build-up
 *  (base fee + per-user -> platform/mo -> period total). Retention is value-set (no cost). */
import React, { useState } from 'react';
import { CRC } from './moduleInputsData';
import { Label, Segmented } from './protoAtoms';
import { SpreadPriceState } from './useSpreadPrice';

const card: React.CSSProperties = { background: '#fff', border: `1px solid ${CRC.border}`, borderTop: `3px solid ${CRC.blueprintBlue}`, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: 20 };
const num = (v: unknown): number => Number((v as number) ?? 0);
const usd = (n: number): string => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PLATFORM_BASE = 'Platform base fee';
const PER_USER = 'Per-user fee';
const RETENTION = 'Data retention (post-handover)';

interface Comp { label: string; baseInfraMonthly?: number; infraBaseline?: number; perModuleInfraMonthly?: number; infraModuleCount?: number; baseSupportHours?: number; supportHourlyRate?: number; baseCost?: number; baseFeeGross?: number; marginTarget?: number; users?: number; perUserMonthly?: number; tier?: string; annual?: number; years?: number; contractTotal?: number; }

const Row: React.FC<{ label: string; desc?: string; value: string; strong?: boolean }> = ({ label, desc, value, strong }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr auto', gap: 10, alignItems: 'baseline', padding: '4px 0', borderBottom: `1px solid ${CRC.rowDivider}` }}>
    <span style={{ fontSize: 12, fontWeight: strong ? 700 : 400, color: strong ? CRC.blueprintBlue : CRC.textGrey }}>{label}</span>
    <span style={{ fontSize: 10.5, color: CRC.heritageGrey }}>{desc}</span>
    <span style={{ fontFamily: 'Funnel Sans', fontSize: 12.5, fontWeight: 600, color: strong ? CRC.readableRed : CRC.blueprintBlue }}>{value}</span>
  </div>
);

interface Props { state: SpreadPriceState; module: 'platform' | 'retention'; label: string; dataModules?: string[]; }

export const SpreadProjectLevelLive: React.FC<Props> = ({ state, module, label, dataModules = [] }) => {
  const [view, setView] = useState<'price' | 'cost'>('price');
  const result = state.status === 'done' ? state.result[module] : null;
  const pl = (result?.details ?? {}) as Record<string, unknown>;
  const recurring = (pl.recurring ?? {}) as Record<string, unknown>;
  const months = num(recurring.months);
  const discountPct = num(pl.discountPercentage);
  const factor = 1 - discountPct / 100;
  const comps = (recurring.components ?? []) as Comp[];
  const base = comps.find((c) => c.label === PLATFORM_BASE);
  const retention = comps.find((c) => c.label === RETENTION);

  const platformCost = () => {
    if (!base) return <div style={{ fontSize: 12.5, color: CRC.heritageGrey }}>No platform fee for this spread.</div>;
    const infra = num(base.baseInfraMonthly);
    const perModule = num(base.perModuleInfraMonthly);
    const count = num(base.infraModuleCount);
    const modulesLabel = dataModules.length ? `${count} (${dataModules.join(', ')})` : `${count} data modules`;
    const infraDesc = infra <= 0 ? 'customer-managed (dropped)'
      : perModule > 0 ? `${usd(num(base.infraBaseline))} baseline + ${usd(perModule)} x ${modulesLabel}`
      : 'fixed / mo';
    return (
      <>
        <Row label="Infra" desc={infraDesc} value={usd(infra)} />
        <Row label="Support" desc={`${num(base.baseSupportHours)} hrs x ${usd(num(base.supportHourlyRate))}/hr`} value={usd(num(base.baseSupportHours) * num(base.supportHourlyRate))} />
        <Row label="Cost / mo" value={usd(num(base.baseCost))} strong />
        <Row label="Platform cost (COGS)" desc={`cost/mo x ${months} mo`} value={usd(result ? result.cost : 0)} strong />
      </>
    );
  };

  const platformPrice = () => {
    if (!base) return <div style={{ fontSize: 12.5, color: CRC.heritageGrey }}>No platform fee for this spread.</div>;
    const perUser = comps.find((c) => c.label === PER_USER);
    const baseFee = num(base.baseFeeGross);
    const users = num(perUser?.users);
    const perUserRate = factor > 0 ? num(perUser?.perUserMonthly) / factor : 0; // undiscounted rate
    const perUserMo = perUserRate * users;
    return (
      <>
        <Row label="Base fee" desc={`cost / (1 - ${Math.round(num(base.marginTarget) * 100)}% margin)`} value={usd(baseFee)} />
        <Row label="Per-user" desc={`${usd(perUserRate)} x ${users} users`} value={usd(perUserMo)} />
        <Row label="Platform / mo" value={usd(baseFee + perUserMo)} strong />
        <Row label="Platform total" desc={`x ${months} mo, less ${discountPct}%`} value={usd(result ? result.total : 0)} strong />
      </>
    );
  };

  const retentionRows = (isCost: boolean) => {
    if (isCost) return <Row label="Retention cost (COGS)" desc="value-set fee, no internal cost" value={usd(0)} strong />;
    if (!retention) return <div style={{ fontSize: 12.5, color: CRC.heritageGrey }}>No retention for this spread (set a tier).</div>;
    return (
      <>
        <Row label="Retention" desc={`${retention.tier ?? ''} ${usd(num(retention.annual))}/yr x ${num(retention.years)} yrs`} value={usd(num(retention.contractTotal))} />
        <Row label="Retention total" desc="not discounted" value={usd(result ? result.total : 0)} strong />
      </>
    );
  };

  const rows = () => {
    if (module === 'retention') return retentionRows(view === 'cost');
    return view === 'cost' ? platformCost() : platformPrice();
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue }}>{label} fee build-up</div>
        {result && (
          <div><Label>View</Label><div style={{ marginTop: 4 }}>
            <Segmented value={view} onChange={(v) => setView(v as 'price' | 'cost')} options={[{ value: 'price', label: 'Price' }, { value: 'cost', label: 'Cost' }]} />
          </div></div>
        )}
      </div>
      <div style={{ marginTop: 14 }}>
        {state.status !== 'done' && state.status !== 'error' && <div style={{ fontSize: 13, color: CRC.heritageGrey }}>Calculating from the pricing engine…</div>}
        {state.status === 'error' && (
          <div style={{ fontSize: 12.5, color: CRC.readableRed, background: 'rgba(196,0,31,0.06)', borderRadius: 8, padding: '10px 12px' }}>Could not reach the pricing engine: {state.error}.</div>
        )}
        {result && rows()}
      </div>
      {result && (
        <div style={{ marginTop: 10, fontSize: 10.5, color: CRC.heritageGrey }}>
          Period total uses the project duration ({months} mo) and its discount band. The platform fee is discounted; retention is not.
        </div>
      )}
    </div>
  );
};
